/* eslint-disable react/prop-types */
import React from 'react';
import {
  Paper,
  Box,
  Typography,
  Button,
  Avatar,
  Slide,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
  CalendarMonth as CalendarIcon,
  Autorenew as RenewIcon,
} from '@mui/icons-material';

const StatusCard = ({ plan, session, onRenewClick, loading = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const getStatusIcon = (status) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVO':
        return <PersonIcon sx={{ fontSize: 28 }} />;
      case 'INACTIVO':
        return <AccessTimeIcon sx={{ fontSize: 28 }} />;
      default:
        return <PersonIcon sx={{ fontSize: 28 }} />;
    }
  };

  const getFinalDate = (date) => {
    if (!date) return '';
    const now = new Date(date);
    now.setFullYear(now.getFullYear() + 1);
    return now.toLocaleDateString('es', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusText = () => {
    if (session?.plan_id) {
      return session?.is_afiliate
        ? 'AFILIADO'
        : plan?.description?.toUpperCase();
    }
    return 'BENEFICIARIO';
  };

  return (
    <Slide direction="up" in={true} timeout={800}>
      <Paper
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #94B754 0%, #7DA647 100%)',
          borderRadius: 4,
          p: { xs: 3, md: 4 },
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(148, 183, 84, 0.3)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -50,
            right: -50,
            width: 150,
            height: 150,
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '50%',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: -30,
            left: -30,
            width: 100,
            height: 100,
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '50%',
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              gap: 3,
            }}
          >
            {/* Status Info */}
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    mr: 2,
                    width: { xs: 48, md: 56 },
                    height: { xs: 48, md: 56 },
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  {getStatusIcon(plan?.description)}
                </Avatar>
                <Box>
                  <Typography
                    variant="body1"
                    sx={{
                      opacity: 0.9,
                      fontSize: { xs: '0.9rem', md: '1rem' },
                    }}
                  >
                    Tu estado actual
                  </Typography>
                  <Typography
                    variant={isMobile ? 'h5' : 'h4'}
                    sx={{
                      fontWeight: 'bold',
                      lineHeight: 1.2,
                    }}
                  >
                    {getStatusText()}
                  </Typography>
                </Box>
              </Box>

              {/* Validity Date */}
              {plan?.description?.toUpperCase() !== 'NO AFILIADO' &&
                plan?.create_at && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                    <CalendarIcon sx={{ mr: 1, opacity: 0.8, fontSize: 20 }} />
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: { xs: '0.85rem', md: '0.9rem' },
                      }}
                    >
                      Válido hasta: {getFinalDate(plan.create_at)}
                    </Typography>
                  </Box>
                )}
            </Box>

            {/* Renew Button */}
            <Box
              sx={{
                width: { xs: '100%', md: 'auto' },
                minWidth: { md: 200 },
              }}
            >
              <Button
                variant="contained"
                size="large"
                fullWidth={isMobile}
                onClick={onRenewClick}
                disabled={loading}
                startIcon={<RenewIcon />}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontWeight: 'bold',
                  py: { xs: 1.5, md: 2 },
                  px: { xs: 2, md: 3 },
                  borderRadius: 3,
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  textTransform: 'none',
                  fontSize: { xs: '0.95rem', md: '1rem' },
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.3)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                  },
                  '&:disabled': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                    color: 'rgba(255,255,255,0.7)',
                  },
                }}
              >
                {loading ? 'Cargando...' : 'Renovar Plan'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Slide>
  );
};

export default StatusCard;
